import { collection, addDoc, updateDoc, doc, getDocs, query, where, Timestamp, deleteDoc, serverTimestamp } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { httpsCallable } from 'firebase/functions';
import { db, storage, functions } from '../lib/firebase';
import { geocodeAddress, capitalizeText } from '../lib/helpers';
import { CATEGORY_VALUES } from '../constants/categories';

// Style value must match category slug for mobile app compatibility
const categoryToStyle = Object.fromEntries(CATEGORY_VALUES.map(v => [v, v]));


// Compress image client-side using Canvas API before upload.
// Skips compression if the image is already within acceptable limits.
// Always falls back to the original file on any error.
const compressImage = (file, maxWidth = 1200, quality = 0.82) => {
  return new Promise((resolve) => {
    const objectUrl = URL.createObjectURL(file);
    const img = new Image();

    img.onload = () => {
      URL.revokeObjectURL(objectUrl);

      // Already small enough — skip compression
      if (img.width <= maxWidth && file.size <= 500 * 1024) {
        resolve(file);
        return;
      }

      const scale = Math.min(1, maxWidth / img.width);
      const canvas = document.createElement('canvas');
      canvas.width = Math.round(img.width * scale);
      canvas.height = Math.round(img.height * scale);

      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

      canvas.toBlob(
        (blob) => resolve(blob || file),
        'image/jpeg',
        quality
      );
    };

    img.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      resolve(file);
    };

    img.src = objectUrl;
  });
};

// Upload image to Firebase Storage - MUST match mobile app path structure
// Path: event_images/{timestamp}.jpg
export const uploadEventImage = async (imageFile) => {
  if (!imageFile) return '';

  try {
    const timestamp = Date.now();
    // Use exact same path structure as mobile app: event_images/{timestamp}.jpg
    const imagePath = `event_images/${timestamp}.jpg`;
    const storageRef = ref(storage, imagePath);

    const imageToUpload = await compressImage(imageFile);

    const snapshot = await uploadBytes(storageRef, imageToUpload);
    const downloadUrl = await getDownloadURL(snapshot.ref);

    return downloadUrl;
  } catch (error) {
    console.error('Error uploading image:', error);
    throw error;
  }
};

function base64ToBlob(base64, contentType) {
  const byteChars = atob(base64);
  const byteNumbers = new Array(byteChars.length);
  for (let i = 0; i < byteChars.length; i++) {
    byteNumbers[i] = byteChars.charCodeAt(i);
  }
  return new Blob([new Uint8Array(byteNumbers)], { type: contentType });
}

const fetchExternalImageCallable = httpsCallable(functions, 'fetchExternalImage');

// Baixa uma imagem de URL externa (import de eventos via JSON/IA) através da
// Cloud Function fetchExternalImage — contorna CORS, já que a origem real
// (Sympla, Instagram, site do organizador etc.) normalmente não libera
// requisições feitas direto pelo navegador. Depois de baixada, reaproveita
// exatamente o mesmo uploadEventImage() (compressão + upload) usado pelo
// fluxo manual de criar/editar evento.
export const reuploadExternalImage = async (externalImageUrl) => {
  const result = await fetchExternalImageCallable({ imageUrl: externalImageUrl });
  const { base64, contentType } = result.data;
  const blob = base64ToBlob(base64, contentType || 'image/jpeg');
  return uploadEventImage(blob);
};

// Create a safe date WITHOUT timezone shift, with an explicit time embedded.
// This ensures the date displayed matches the date selected by user, and the
// hour matches the Flutter app's pattern (time embedded in the Timestamp,
// no separate "time" field).
const createSafeDate = (dateInput, hours = 0, minutes = 0, seconds = 0) => {
  if (!dateInput) return null;

  // If it's already a Firestore Timestamp, extract the date parts
  if (dateInput.toDate) {
    const d = dateInput.toDate();
    return new Date(d.getFullYear(), d.getMonth(), d.getDate(), hours, minutes, seconds, 0);
  }

  // If it's a string like "2025-12-25", parse it WITHOUT timezone conversion
  if (typeof dateInput === 'string') {
    const parts = dateInput.split('-');
    if (parts.length === 3) {
      const year = parseInt(parts[0], 10);
      const month = parseInt(parts[1], 10) - 1; // Month is 0-indexed
      const day = parseInt(parts[2], 10);
      return new Date(year, month, day, hours, minutes, seconds, 0);
    }
  }

  // If it's a Date object, extract components to avoid timezone issues
  if (dateInput instanceof Date) {
    return new Date(dateInput.getFullYear(), dateInput.getMonth(), dateInput.getDate(), hours, minutes, seconds, 0);
  }

  // Fallback
  const d = new Date(dateInput);
  return new Date(d.getFullYear(), d.getMonth(), d.getDate(), hours, minutes, seconds, 0);
};

// Parses an ISO-like date or datetime string (e.g. "2026-11-10" or
// "2026-11-10T23:00:00") into a local Date, WITHOUT letting the native Date
// engine apply its UTC-vs-local ambiguity for date-only strings (per the
// JS spec, "YYYY-MM-DD" is parsed as UTC, while "YYYY-MM-DDTHH:mm:ss" without
// an offset is parsed as local time — mixing both in the same dataset is
// exactly what causes date/startDate to drift by a day). Year/month/day/
// hour/minute/second are extracted manually and fed to the local Date
// constructor, same strategy as createSafeDate. Any trailing "Z"/offset in
// the string is intentionally ignored — the numbers are always read as the
// intended local wall-clock time, matching every other flow in this app.
export const parseSafeDateTime = (dateInput) => {
  if (!dateInput) return null;

  if (dateInput.toDate) return dateInput.toDate();
  if (dateInput instanceof Date) return dateInput;

  if (typeof dateInput === 'string') {
    const match = dateInput.match(/^(\d{4})-(\d{2})-(\d{2})(?:[T ](\d{2}):(\d{2})(?::(\d{2}))?)?/);
    if (match) {
      const year = parseInt(match[1], 10);
      const month = parseInt(match[2], 10) - 1;
      const day = parseInt(match[3], 10);
      const hours = match[4] !== undefined ? parseInt(match[4], 10) : 0;
      const minutes = match[5] !== undefined ? parseInt(match[5], 10) : 0;
      const seconds = match[6] !== undefined ? parseInt(match[6], 10) : 0;
      return new Date(year, month, day, hours, minutes, seconds, 0);
    }
  }

  // Fallback for unexpected formats
  return new Date(dateInput);
};

// Parse a "HH:MM" string (from the Horário Início/Final inputs) into hour/
// minute parts. Falls back to 00:00 when missing or invalid.
export const parseTimeParts = (timeStr) => {
  if (typeof timeStr === 'string' && timeStr.includes(':')) {
    const [h, m] = timeStr.split(':').map(Number);
    if (!Number.isNaN(h) && !Number.isNaN(m)) {
      return { hours: h, minutes: m };
    }
  }
  return { hours: 0, minutes: 0 };
};

export const eventService = {
  async createEvent(eventData, imageFile, initialStatus = 'pending') {
    try {
      // Upload image using mobile app compatible path
      const imageUrl = await uploadEventImage(imageFile);

      // Generate citySlug from city (lowercase for mobile app filtering)
      const citySlug = eventData.city.toLowerCase();
      
      // Generate style from category (style MUST match category)
      const style = categoryToStyle[eventData.category] || 'outros';
      
      // Convert isPaid to isFree (inverted logic for mobile app)
      const isFree = !eventData.isPaid;
      
      // Geocode address to get real coordinates
      const coords = await geocodeAddress(eventData.location, eventData.city);

      // IMPORTANT: Create safe date WITHOUT timezone shift, with the real
      // hour embedded in the Timestamp (same pattern as the Flutter app —
      // no separate "time" field).
      const { hours, minutes } = parseTimeParts(eventData.time);
      const { hours: endHours, minutes: endMinutes } = parseTimeParts(eventData.endTime);
      const safeDate = createSafeDate(eventData.date, hours, minutes);
      const dateTimestamp = Timestamp.fromDate(safeDate);

      const safeStartDate = createSafeDate(eventData.startDate || eventData.date, hours, minutes);
      const safeEndDate = createSafeDate(eventData.endDate || eventData.date, endHours, endMinutes);

      const startDateTimestamp = Timestamp.fromDate(safeStartDate);
      const endDateTimestamp = Timestamp.fromDate(safeEndDate);

      const eventDoc = {
        title: capitalizeText(eventData.title),  // Capitalize title
        description: eventData.description,
        category: eventData.category,
        style: style,  // Required by mobile app
        city: eventData.city,
        citySlug: citySlug,  // Required for mobile app filtering
        location: capitalizeText(eventData.location),  // Capitalize location
        latitude: coords.latitude,  // Real coordinates from geocoding
        longitude: coords.longitude,  // Real coordinates from geocoding
        date: dateTimestamp,  // Same instant as startDate, mirroring the Flutter app
        startDate: startDateTimestamp,
        endDate: endDateTimestamp,
        isFree: isFree,  // Mobile app uses isFree instead of isPaid
        ticketPrice: eventData.ticketPrice || 0,  // Required by mobile app
        ticketUrl: eventData.ticketUrl || '',  // Required by mobile app
        isHighlighted: eventData.isHighlighted || false,
        imageUrl,
        organizerId: eventData.createdBy,  // PRIMARY ownership field (mobile app standard)
        createdBy: eventData.createdBy,    // LEGACY field (backward compatibility)
        status: initialStatus,
        createdAt: serverTimestamp(),
        lastUpdated: serverTimestamp()
      };

      const docRef = await addDoc(collection(db, 'events'), eventDoc);
      return { success: true, id: docRef.id };
    } catch (error) {
      console.error('Error creating event:', error);
      console.error('Error details:', error.message);
      return { success: false, error: error.message };
    }
  },

  async updateEvent(eventId, eventData, imageFile) {
    try {
      // Generate citySlug from city (lowercase for mobile app filtering)
      const citySlug = eventData.city.toLowerCase();
      
      // Generate style from category
      const style = categoryToStyle[eventData.category] || 'outros';
      
      // Convert isPaid to isFree (inverted logic for mobile app)
      const isFree = !eventData.isPaid;
      
      // Geocode address to get real coordinates
      const coords = await geocodeAddress(eventData.location, eventData.city);

      // IMPORTANT: Create safe date WITHOUT timezone shift, with the real
      // hour embedded in the Timestamp (same pattern as the Flutter app —
      // no separate "time" field).
      const { hours, minutes } = parseTimeParts(eventData.time);
      const { hours: endHours, minutes: endMinutes } = parseTimeParts(eventData.endTime);
      const safeDate = createSafeDate(eventData.date, hours, minutes);
      const safeStartDate = createSafeDate(eventData.startDate || eventData.date, hours, minutes);
      const safeEndDate = createSafeDate(eventData.endDate || eventData.date, endHours, endMinutes);

      const startDateTimestamp = Timestamp.fromDate(safeStartDate);
      const endDateTimestamp = Timestamp.fromDate(safeEndDate);
      const dateTimestamp = Timestamp.fromDate(safeDate);

      const updateData = {
        title: capitalizeText(eventData.title),  // Capitalize title
        description: eventData.description,
        category: eventData.category,
        style: style,  // Required by mobile app
        city: eventData.city,
        citySlug: citySlug,  // Required for mobile app filtering
        location: capitalizeText(eventData.location),  // Capitalize location
        latitude: coords.latitude,  // Real coordinates from geocoding
        longitude: coords.longitude,  // Real coordinates from geocoding
        date: dateTimestamp,  // Same instant as startDate, mirroring the Flutter app
        startDate: startDateTimestamp,
        endDate: endDateTimestamp,
        isFree: isFree,  // Mobile app uses isFree instead of isPaid
        ticketPrice: eventData.ticketPrice || 0,  // Required by mobile app
        ticketUrl: eventData.ticketUrl || '',  // Required by mobile app
        isHighlighted: eventData.isHighlighted || false,
        lastUpdated: serverTimestamp()
      };

      // Upload new image if provided (using mobile app compatible path)
      if (imageFile) {
        updateData.imageUrl = await uploadEventImage(imageFile);
      }

      await updateDoc(doc(db, 'events', eventId), updateData);
      return { success: true };
    } catch (error) {
      console.error('Error updating event:', error);
      return { success: false, error: error.message };
    }
  },

  async getEventsByOrganizer(userId) {
    try {
      // Fetch events using ONLY organizerId (standard field)
      // Assumes backfill has been completed and all events have organizerId
      const q = query(
        collection(db, 'events'),
        where('organizerId', '==', userId)
      );
      
      const snapshot = await getDocs(q);
      let events = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      
      // Sort: Boosted events first, then by createdAt desc
      events.sort((a, b) => {
        // First priority: boosted events
        if (a.boosted && !b.boosted) return -1;
        if (!a.boosted && b.boosted) return 1;
        
        // Second priority: boostValue (higher first)
        if (a.boosted && b.boosted) {
          const aValue = a.boostValue || 0;
          const bValue = b.boostValue || 0;
          if (aValue !== bValue) return bValue - aValue;
        }
        
        // Third priority: createdAt (newer first)
        const timeA = a.createdAt?.toMillis() || 0;
        const timeB = b.createdAt?.toMillis() || 0;
        return timeB - timeA;
      });
      
      return events;
    } catch (error) {
      console.error('Error fetching organizer events:', error);
      return [];
    }
  },

  async getAllEvents() {
    try {
      const q = query(collection(db, 'events'));
      const snapshot = await getDocs(q);
      let events = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      
      // Sort: Boosted events first, then by createdAt desc
      events.sort((a, b) => {
        // First priority: boosted events
        if (a.boosted && !b.boosted) return -1;
        if (!a.boosted && b.boosted) return 1;
        
        // Second priority: boostValue (higher first)
        if (a.boosted && b.boosted) {
          const aValue = a.boostValue || 0;
          const bValue = b.boostValue || 0;
          if (aValue !== bValue) return bValue - aValue;
        }
        
        // Third priority: createdAt (newer first)
        const timeA = a.createdAt?.toMillis() || 0;
        const timeB = b.createdAt?.toMillis() || 0;
        return timeB - timeA;
      });
      
      return events;
    } catch (error) {
      console.error('Error fetching all events:', error);
      return [];
    }
  },

  async updateEventStatus(eventId, status) {
    try {
      await updateDoc(doc(db, 'events', eventId), {
        status,
        lastUpdated: serverTimestamp()
      });
      return { success: true };
    } catch (error) {
      console.error('Error updating event status:', error);
      return { success: false, error: error.message };
    }
  },

  async deleteEvent(eventId) {
    try {
      await deleteDoc(doc(db, 'events', eventId));
      return { success: true };
    } catch (error) {
      console.error('Error deleting event:', error);
      return { success: false, error: error.message };
    }
  }

  // NOTE: boostEvent removed - Boost is now ONLY applied via:
  // 1. Cloud Function: createPixPayment (creates PIX)
  // 2. Mercado Pago webhook (applies boost after payment confirmation)
  // See: /app/PAYMENT_WEBHOOK_GUIDE.md
};

